import { useEffect } from "react";
import { Palmtree, ArrowLeft } from "lucide-react";
import { useSite, BRAND } from "./lib/site";

const UPDATED = "junho de 2026";

const Terms = ({ owner, contact }) => (
  <>
    <p>Estes Termos de Uso regulam a utilização do site {BRAND} e a contratação de hospedagens por temporada anunciadas nele. Ao fazer uma reserva, você declara que leu e concorda com estes termos.</p>
    <h2>1. O serviço</h2>
    <p>O {BRAND} apresenta imóveis para locação por temporada e permite reservá-los mediante o pagamento de um sinal via PIX. A locação é regida pela Lei nº 8.245/1991 (art. 48 a 50) e pelo Código de Defesa do Consumidor.</p>
    <h2>2. Reserva e pagamento</h2>
    <ul>
      <li>A reserva só é garantida após a confirmação do pagamento do sinal, exibida automaticamente na tela.</li>
      <li>O código PIX tem validade de 30 minutos. Após esse prazo, a solicitação é cancelada e as datas voltam a ficar disponíveis.</li>
      <li>O valor restante da estadia deve ser pago no check-in, salvo combinação diferente feita por escrito.</li>
      <li>Os preços podem variar conforme a temporada (feriados, Réveillon, Carnaval etc.). Vale sempre o valor exibido no momento da reserva.</li>
    </ul>
    <h2>3. Responsabilidades do hóspede</h2>
    <ul>
      <li>Informar dados verdadeiros (nome, WhatsApp, e-mail e CPF, quando solicitado).</li>
      <li>Respeitar o número máximo de hóspedes, as regras da casa e os horários de check-in e check-out informados.</li>
      <li>Zelar pelo imóvel e responder por danos causados durante a estadia.</li>
    </ul>
    <h2>4. Cancelamento</h2>
    <p>As regras de cancelamento e reembolso estão na nossa <a href="/cancelamento">Política de Cancelamento</a>.</p>
    <h2>5. Privacidade</h2>
    <p>O tratamento dos seus dados pessoais segue a nossa <a href="/privacidade">Política de Privacidade</a>, em conformidade com a LGPD (Lei nº 13.709/2018).</p>
    <h2>6. Contato</h2>
    <p>Dúvidas sobre estes termos: {contact}. {owner}</p>
  </>
);

const Privacy = ({ owner, contact }) => (
  <>
    <p>Esta Política explica como o {BRAND} coleta, usa e protege seus dados pessoais, de acordo com a Lei Geral de Proteção de Dados (LGPD, Lei nº 13.709/2018).</p>
    <h2>1. Dados que coletamos</h2>
    <ul>
      <li><strong>Na reserva:</strong> nome completo, WhatsApp, e-mail, CPF (opcional) e as datas escolhidas.</li>
      <li><strong>Automaticamente:</strong> endereço IP e dados técnicos de acesso, usados para segurança e prevenção de fraudes.</li>
    </ul>
    <h2>2. Para que usamos</h2>
    <ul>
      <li>Processar e confirmar sua reserva e o pagamento via PIX.</li>
      <li>Entrar em contato sobre check-in, check-out e eventuais alterações.</li>
      <li>Cumprir obrigações legais e prevenir fraudes.</li>
    </ul>
    <h2>3. Compartilhamento</h2>
    <p>Seus dados são compartilhados apenas com o processador de pagamentos (LofyPay), na medida necessária para gerar e confirmar o PIX. Não vendemos nem cedemos seus dados para publicidade.</p>
    <h2>4. Armazenamento e segurança</h2>
    <p>Os dados ficam em servidores protegidos, com acesso restrito por senha e conexão criptografada (HTTPS). Guardamos as informações pelo tempo necessário para a prestação do serviço e para o cumprimento de obrigações legais.</p>
    <h2>5. Seus direitos</h2>
    <p>Você pode, a qualquer momento, pedir acesso, correção, anonimização ou exclusão dos seus dados, bem como informações sobre o compartilhamento, escrevendo para {contact}.</p>
    <h2>6. Controlador</h2>
    <p>{owner || `${BRAND}.`}</p>
  </>
);

const Cancellation = ({ contact }) => (
  <>
    <p>Queremos que sua reserva seja tranquila do começo ao fim. Confira abaixo como funcionam o pagamento, o cancelamento e a remarcação.</p>
    <h2>1. Como funciona o pagamento</h2>
    <ul>
      <li><strong>Sinal:</strong> pago via PIX no site, no momento da reserva. O percentual aparece no resumo antes do pagamento.</li>
      <li><strong>Restante:</strong> pago no check-in.</li>
    </ul>
    <h2>2. Direito de arrependimento (7 dias)</h2>
    <p>Conforme o art. 49 do Código de Defesa do Consumidor, você pode desistir da reserva em até 7 dias corridos após o pagamento do sinal e receber o valor pago de volta integralmente.</p>
    <h2>3. Depois de 7 dias</h2>
    <ul>
      <li>O sinal não é reembolsável, mas pode ser usado uma vez para remarcar a estadia, em até 12 meses, conforme disponibilidade e eventual diferença de valor da nova data.</li>
      <li>Para remarcar, entre em contato com pelo menos 7 dias de antecedência do check-in original.</li>
    </ul>
    <h2>4. Cancelamento pelo anfitrião</h2>
    <p>Se precisarmos cancelar por um motivo de força maior ou por indisponibilidade do imóvel, você recebe a devolução integral do sinal ou pode remarcar sem custo.</p>
    <h2>5. Como solicitar</h2>
    <p>Envie o nome do hóspede e as datas da reserva para {contact}. Os reembolsos são feitos via PIX em até 7 dias úteis.</p>
  </>
);

const PAGES = {
  "/termos": { title: "Termos de Uso", Body: Terms },
  "/privacidade": { title: "Política de Privacidade", Body: Privacy },
  "/cancelamento": { title: "Política de Cancelamento", Body: Cancellation },
};

export const isLegalPath = (path) => Boolean(PAGES[path]);

export default function LegalPage({ path }) {
  const site = useSite();
  const { title, Body } = PAGES[path];
  const contact = [site.email, site.whatsapp && `WhatsApp ${site.whatsapp}`].filter(Boolean).join(" ou ") || "os canais de atendimento informados no site";
  const owner = site.company_name ? `${site.company_name}${site.cnpj ? `, CNPJ ${site.cnpj}` : ""}.` : "";

  useEffect(() => { document.title = `${title} — ${BRAND}`; window.scrollTo(0, 0); }, [title]);

  return (
    <div className="min-h-screen bg-[#FBF8F3] text-slate-800" style={{ fontFamily: "Outfit, system-ui, sans-serif" }}>
      <header className="border-b border-[#EDE6DA] bg-white/80 backdrop-blur-md sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2" data-testid="legal-logo">
            <span className="bg-[#0A66C2] p-1.5 rounded-lg"><Palmtree className="text-white w-5 h-5" /></span>
            <span className="text-lg font-extrabold text-[#0A66C2]">Se Hospede <span className="text-[#FFB800]">AKI</span></span>
          </a>
          <a href="/" className="text-sm font-semibold text-slate-500 hover:text-[#0A66C2] flex items-center gap-1" data-testid="legal-back"><ArrowLeft size={14} /> Voltar ao site</a>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 py-12" data-testid="legal-page">
        <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-2" style={{ fontFamily: "Fraunces, serif" }}>{title}</h1>
        <p className="text-sm text-slate-400 mb-10">Última atualização: {UPDATED}</p>
        <article className="legal-body bg-white rounded-3xl border border-[#EDE6DA] p-6 sm:p-10 leading-relaxed text-[15px]">
          <Body owner={owner} contact={contact} />
        </article>
        <nav className="flex flex-wrap gap-4 justify-center mt-10 text-sm">
          {Object.entries(PAGES).filter(([p]) => p !== path).map(([p, v]) => (
            <a key={p} href={p} className="text-[#0A66C2] font-semibold hover:underline">{v.title}</a>
          ))}
        </nav>
      </main>
    </div>
  );
}
